'use client';

import React, { useState } from 'react';
import Image from 'next/image';

type TeamMember = {
  name: string;
  role: string;
  focus: string;
  credentials: string;
  linkedin: string;
  imageSrc: string;
};

const FOUNDING_TEAM: TeamMember[] = [
  {
    name: 'Umang Agarwal',
    role: 'Co-Founder',
    focus: 'BD & Product',
    credentials: 'Ex-P&G · IIM Calcutta · Ramjas',
    linkedin: 'https://in.linkedin.com/in/umang-agarwal-iimcal',
    imageSrc: '/team/umang-agarwal.jpg',
  },
  {
    name: 'Rohan Prakash',
    role: 'Co-Founder',
    focus: 'Product & Technology',
    credentials: 'Ex-Tata · IIM Calcutta · IIT Delhi',
    linkedin: 'https://in.linkedin.com/in/rohan-prakash-6a035b179',
    imageSrc: '/team/rohan-prakash.jpg',
  },
  {
    name: 'Kunal Gupta',
    role: 'Co-Founder',
    focus: 'Strategy & Ops',
    credentials: 'Ex-BCG · XLRI · SRCC',
    linkedin: 'https://in.linkedin.com/in/kunal-gupta-50513b169',
    imageSrc: '/team/kunal-gupta.jpg',
  },
];

const LinkedInIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6.94 8.5H3.5V21h3.44V8.5ZM5.22 3C4.08 3 3.15 3.93 3.15 5.07c0 1.13.92 2.06 2.06 2.06 1.14 0 2.06-.93 2.06-2.06C7.27 3.93 6.36 3 5.22 3ZM20.85 14.1c0-3.2-1.7-4.7-4-4.7-1.85 0-2.67 1.02-3.13 1.73V8.5H10.3c.05.86 0 12.5 0 12.5h3.42v-6.98c0-.37.03-.73.14-.99.3-.73.98-1.49 2.13-1.49 1.5 0 2.1 1.14 2.1 2.8V21h3.42v-6.9Z" />
  </svg>
);

const TeamCard: React.FC<{ member: TeamMember }> = ({ member }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const initials = member.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <article className="team-member">
      <div className={`team-member__photo-wrap${imageFailed ? ' is-fallback' : ''}`}>
        {imageFailed ? (
          <div className="team-member__fallback" aria-hidden="true">
            {initials}
          </div>
        ) : (
          <Image
            src={member.imageSrc}
            alt={`${member.name}, ${member.role} at IntelGrader`}
            className="team-member__photo"
            width={300}
            height={300}
            onError={() => setImageFailed(true)}
          />
        )}
      </div>

      <div className="team-member__body">
        <div className="team-member__intro">
          <h2 className="team-member__name">{member.name}</h2>
          <p className="team-member__role">{member.role}</p>
        </div>

        <p className="team-member__focus">{member.focus}</p>
        <p className="team-member__credentials">{member.credentials}</p>

        <a
          href={member.linkedin}
          target="_blank"
          rel="noreferrer"
          className="team-member__link"
          aria-label={`${member.name} on LinkedIn`}
        >
          <span className="team-member__link-icon">
            <LinkedInIcon />
          </span>
          <span>LinkedIn</span>
        </a>
      </div>
    </article>
  );
};

const Team: React.FC = () => {
  return (
    <section className="team-page team-page--v2">
      <div className="team-page__inner">
        <div className="team-page__band">
          <div className="team-page__header team-page__header--center">
            <p className="team-page__eyebrow">Founding Team</p>
            <h1 className="team-page__title">Built by operators, technologists, and education builders.</h1>
            <p className="team-page__subhead">
              A founding team spanning product, strategy, engineering, and go-to-market for
              tutoring and learning intelligence.
            </p>
          </div>

          <blockquote className="team-page__quote">
            <p className="team-page__quote-text">
              &ldquo;We want to supercharge education, one paper at a time.&rdquo;
            </p>
            <p className="team-page__quote-support">
              IntelGrader exists to help teachers and students everywhere understand learning
              health more clearly, so they can learn, adapt, and grow faster.
            </p>
          </blockquote>

          <div className="team-page__grid" aria-label="IntelGrader founding team">
            {FOUNDING_TEAM.map((member) => (
              <TeamCard key={member.name} member={member} />
            ))}
          </div>
        </div>

        <a href="/" className="btn btn-secondary team-page__back">
          Back to Home
        </a>
      </div>
    </section>
  );
};

export default Team;
